import ActionButton from "components/Buttons/ActionButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";

interface Props {
  show: boolean;
  onClose: () => void;
  url?: string | null;
}

const FilePreviewModal: React.FC<Props> = ({ show, onClose, url }) => {
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let currentBlobUrl: string | null = null;

    // Reset states when modal opens/closes or URL changes
    if (show && url) {
      setImageError(false);
      setLoading(true);
      setBlobUrl(null);

      // Fetch the file and convert to blob URL
      const fetchFile = async () => {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch file: ${response.status} ${response.statusText}`
            );
          }
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          currentBlobUrl = objectUrl;
          setBlobUrl(objectUrl);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching file:", error);
          setLoading(false);
          setImageError(true);
        }
      };

      fetchFile();
    } else {
      setBlobUrl(null);
    }

    // Cleanup: revoke object URL when component unmounts or modal closes
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [show, url]);

  const isPDF = url?.toLowerCase().endsWith(".pdf") || false;
  const fileExtension = url?.split(".").pop()?.toLowerCase() || "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(
    fileExtension
  );

  const handleImageLoad = () => {
    setLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setLoading(false);
    setImageError(true);
  };

  const handleDownload = async () => {
    if (!url) return;

    try {
      // Use blob URL if available, otherwise fetch
      if (blobUrl) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = url.split("/").pop() || "file";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        // Fallback: fetch and download
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error("Failed to fetch file");
        }
        const blob = await res.blob();
        const downloadBlobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = downloadBlobUrl;
        link.download = url.split("/").pop() || "file";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadBlobUrl);
      }
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open in new tab
      window.open(url, "_blank");
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Document Preview</Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        {!url ? (
          <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
            <i className="bi bi-exclamation-triangle fs-3 mb-2"></i>
            <p>No document URL provided</p>
          </div>
        ) : imageError ? (
          <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
            <i className="bi bi-exclamation-triangle fs-3 mb-2"></i>
            <p>Failed to load preview</p>
            <p
              style={{
                fontSize: "12px",
                wordBreak: "break-all",
                marginTop: "10px",
              }}
            >
              URL: {url}
            </p>
            <div className="mt-3 d-flex gap-2 justify-content-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleDownload}
              >
                Download File
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  window.open(url || "", "_blank");
                }}
              >
                Open in New Tab
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading preview...</p>
          </div>
        ) : isPDF ? (
          <iframe
            src={blobUrl || url || ""}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            title="PDF Preview"
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setImageError(true);
            }}
          />
        ) : isImage ? (
          <img
            src={blobUrl || url || ""}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : (
          <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
            <i className="bi bi-file-earmark fs-3 mb-2"></i>
            <p>Preview not available for this file type</p>
            <p style={{ fontSize: "12px", marginTop: "10px" }}>
              File: {url.split("/").pop()}
            </p>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownload}
              className="mt-3"
            >
              Download File
            </Button>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-center gap-3">
        {url && (
          <ActionButton
            variant="primary"
            handleClick={handleDownload}
            label="Download"
          />
        )}
        <CancelBackButton label="Close" onClick={onClose} />
      </Modal.Footer>
    </Modal>
  );
};

export default FilePreviewModal;
